"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useRef, useState } from "react";
import Input from "@/components/ui/input";
import { cx } from "@/lib/tailwind-utils";
import { FilterInputProps } from "./types";
type NumberComparison = "eq" | "gte" | "lte";
interface NumberFilterValue {
  value: number;
  comparison: NumberComparison;
}
const COMPARISON_OPTIONS = [{
  value: "eq" as const,
  label: "=",
  title: "Equals"
}, {
  value: "gte" as const,
  label: "≥",
  title: "Greater than or equal"
}, {
  value: "lte" as const,
  label: "≤",
  title: "Less than or equal"
}];
const DEBOUNCE_MS = 500;

/**
 * Number filter component with comparison operators (=, ≥, ≤).
 * Applies filter after typing stops (debounced), on blur, or Enter key.
 */
const NumberFilter: React.FC<FilterInputProps> = t0 => {
  const $ = _c(33);
  const {
    value,
    onChange,
    onBack,
    focusRef,
    onSubmit
  } = t0;
  const initialComparison = typeof value === "object" && value?.comparison ? value.comparison : "eq";
  const initialValue = typeof value === "object" && value?.value !== undefined ? String(value.value) : "";
  const [comparison, setComparison] = useState(initialComparison);
  const [numberValue, setNumberValue] = useState(initialValue);
  const [hasInput, setHasInput] = useState(false);
  const debounceTimeoutRef = useRef(null);
  const lastAppliedRef = useRef(null);
  let t1;
  if ($[0] !== onChange) {
    t1 = (comp, val, userInput) => {
      if (!userInput && val === "") {
        return;
      }
      const applyKey = val === "" ? "undefined" : `${comp}:${val}`;
      if (lastAppliedRef.current === applyKey) {
        return;
      }
      lastAppliedRef.current = applyKey;
      if (val === "") {
        onChange(undefined);
      } else {
        const numVal = parseFloat(val);
        if (!isNaN(numVal)) {
          onChange({
            value: numVal,
            comparison: comp
          } as NumberFilterValue);
        }
      }
    };
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const applyFilter = t1;
  let t2;
  if ($[2] !== applyFilter) {
    t2 = (comp_0, val_0) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        applyFilter(comp_0, val_0, true);
      }, DEBOUNCE_MS);
    };
    $[2] = applyFilter;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const debouncedApply = t2;
  let t3;
  if ($[4] !== comparison || $[5] !== debouncedApply) {
    t3 = e => {
      const newValue = e.target.value;
      setNumberValue(newValue);
      setHasInput(true);
      debouncedApply(comparison, newValue);
    };
    $[4] = comparison;
    $[5] = debouncedApply;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleChange = t3;
  let t4;
  if ($[7] !== applyFilter || $[8] !== comparison || $[9] !== hasInput || $[10] !== numberValue) {
    t4 = () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      applyFilter(comparison, numberValue, hasInput);
    };
    $[7] = applyFilter;
    $[8] = comparison;
    $[9] = hasInput;
    $[10] = numberValue;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleBlur = t4;
  let t5;
  if ($[12] !== applyFilter || $[13] !== comparison || $[14] !== numberValue || $[15] !== onBack || $[16] !== onSubmit) {
    t5 = e_0 => {
      if (e_0.key === "Enter") {
        e_0.preventDefault();
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
        applyFilter(comparison, numberValue, true);
        onSubmit?.();
      } else {
        if (e_0.key === "ArrowLeft" && onBack) {
          const target = e_0.target as HTMLInputElement;
          if (target.selectionStart === 0 && target.selectionEnd === 0) {
            e_0.preventDefault();
            onBack();
          }
        }
      }
    };
    $[12] = applyFilter;
    $[13] = comparison;
    $[14] = numberValue;
    $[15] = onBack;
    $[16] = onSubmit;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const handleKeyDown = t5;
  let t6;
  let t7;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
    t7 = [];
    $[18] = t6;
    $[19] = t7;
  } else {
    t6 = $[18];
    t7 = $[19];
  }
  React.useEffect(t6, t7);
  let t8;
  if ($[20] !== comparison) {
    t8 = COMPARISON_OPTIONS.map(comp_1 => <button key={comp_1.value} type="button" title={comp_1.title} onClick={() => setComparison(comp_1.value)} className={cx("flex h-8 w-10 items-center justify-center rounded-md border text-body-strong transition-colors", comparison === comp_1.value ? "border-primary bg-primary-50 text-primary dark:bg-primary-900/30" : "border-border bg-background text-foreground-secondary hover:bg-hover")}>{comp_1.label}</button>);
    $[20] = comparison;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t8) {
    t9 = <div className="flex gap-1">{t8}</div>;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== focusRef || $[25] !== handleBlur || $[26] !== handleChange || $[27] !== handleKeyDown || $[28] !== numberValue) {
    t10 = <Input type="number" value={numberValue} onChange={handleChange} onBlur={handleBlur} onKeyDown={handleKeyDown} ref={focusRef} placeholder="Enter a number..." autoFocus={true} className="!border-none !bg-transparent !shadow-none !ring-0 focus:!ring-0" />;
    $[24] = focusRef;
    $[25] = handleBlur;
    $[26] = handleChange;
    $[27] = handleKeyDown;
    $[28] = numberValue;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t9) {
    t11 = <div className="space-y-2">{t9}{t10}</div>;
    $[30] = t10;
    $[31] = t9;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
};
export default NumberFilter;